import { useState, useEffect, useRef, useCallback } from "react";
import type { SuggestionResponse } from "../types/api";

interface UseRealTimeSuggestionsOptions {
  documentId: string | null;
  orgId: string | null;
  content: string;
  enabled: boolean;
  debounceMs?: number;
  onSuggestionsGenerated?: (suggestions: SuggestionResponse[]) => void;
}

interface UseRealTimeSuggestionsResult {
  isGenerating: boolean;
  lastGeneratedAt: Date | null;
  error: string | null;
  isAINotConfigured: boolean;
  triggerGeneration: () => void;
}

const DEFAULT_DEBOUNCE_MS = 500;

export function useRealTimeSuggestions({
  documentId,
  orgId,
  content,
  enabled,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onSuggestionsGenerated,
}: UseRealTimeSuggestionsOptions): UseRealTimeSuggestionsResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAINotConfigured, setIsAINotConfigured] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastContentRef = useRef<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateSuggestions = useCallback(async () => {
    if (!documentId || !orgId || !content.trim() || isAINotConfigured) {
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/organizations/${orgId}/documents/${documentId}/suggestions/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ content }),
          signal: abortControllerRef.current.signal,
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (
          data.code === "AI_NOT_CONFIGURED" ||
          data.code === "AI_AUTH_ERROR" ||
          response.status === 404 ||
          response.status === 503
        ) {
          setIsAINotConfigured(true);
          setError("Connect an AI provider in Settings to enable suggestions.");
          setIsGenerating(false);
          return;
        }
        if (
          response.status === 502 ||
          data.code === "AI_SERVICE_ERROR" ||
          data.code === "AI_UPSTREAM_ERROR"
        ) {
          setError(
            "AI service is currently unavailable. Suggestions will resume when the connection is restored.",
          );
          setIsGenerating(false);
          return;
        }
        if (data.code === "AI_OVERLOADED") {
          setError("AI service is temporarily overloaded. Suggestions will retry shortly.");
          setIsGenerating(false);
          return;
        }
        if (response.status >= 500) {
          throw new Error("AI service is temporarily unavailable. Please try again later.");
        }
        throw new Error(data.error || `Failed to generate suggestions: ${response.status}`);
      }

      const data = await response.json();
      const suggestions: SuggestionResponse[] = data.suggestions || [];

      setLastGeneratedAt(new Date());
      onSuggestionsGenerated?.(suggestions);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // Request was cancelled, don't set error
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to generate suggestions");
    } finally {
      setIsGenerating(false);
    }
  }, [documentId, orgId, content, isAINotConfigured, onSuggestionsGenerated]);

  // Debounced content change handler
  useEffect(() => {
    if (!enabled || content === lastContentRef.current) {
      return;
    }

    lastContentRef.current = content;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      generateSuggestions();
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [content, enabled, debounceMs, generateSuggestions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const triggerGeneration = useCallback(() => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    generateSuggestions();
  }, [generateSuggestions]);

  return {
    isGenerating,
    lastGeneratedAt,
    error,
    isAINotConfigured,
    triggerGeneration,
  };
}
